import React from "react";
import classNames from "classnames";
import OpticksIconText from "@/components/ui/OpticksIconText/OpticksIconText";
import "./OpticksButtonGroup.css";

export interface ButtonGroupOption {
  label: string;
  key: string;
  image?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface OpticksButtonGroupProps {
  options: ButtonGroupOption[];
  selectedOption: string;
  onSelectionChange: (key: string) => void;
  disabled?: boolean;
  variation?: "primary" | "secondary" | "traffic";
  className?: string;
  size?: "s" | "image";
  surface?: "default" | "white";
}

const OpticksButtonGroup = ({
  options,
  selectedOption,
  onSelectionChange,
  disabled,
  variation = "primary",
  className,
  size = "s",
  surface,
}: OpticksButtonGroupProps) => {
  if (
    process.env.NODE_ENV !== "production" &&
    variation === "traffic" &&
    options.length > 3
  ) {
    console.warn(
      'OpticksButtonGroup: "traffic" variation colours by POSITION — legitimate, suspicious, invalid — so it fits 2 or 3 options.'
    );
  }

  return (
    <div
      role="group"
      data-variation={variation}
      className={classNames(
        "opticks-button-group",
        { [`size-${size}`]: size },
        { [`surface-${surface}`]: surface },
        className
      )}
    >
      {options.map((option) => {
        const isSelected = selectedOption === option.key;

        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={isSelected}
            className={
              isSelected
                ? "opticks-button-group-item--selected"
                : "opticks-button-group-item--unselected"
            }
            onClick={() => onSelectionChange(option.key)}
            disabled={disabled || option.disabled}
          >
            <div
              className={classNames("opticks-button-group-item-content", {
                "with-image": option.image && !option.icon,
              })}
            >
              {option.icon ? (
                <OpticksIconText icon={option.icon} text={option.label} />
              ) : (
                <>
                  {option.image && <div>{option.image}</div>}
                  <b>{option.label}</b>
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default OpticksButtonGroup;
